<template>
  <div
    class="MyMapIndicateur"
    :id="indicateur.atgId.toString()"
    :class="{
      'MyMapIndicateur--clickable': isClickable,
      'MyMapIndicateur--active': isVisible,
    }"
    :atg-geographic-scope="geographicScope"
    @click="handleClick"
  >
    <div class="MyMapIndicateur__content">
      <div class="MyMapIndicateur__icon">
        <Icon :icon="indicateurIcon" color="main-blue" :size="18" />
      </div>
      <div class="MyMapIndicateur__text">
        <span class="MyMapIndicateur__titre" :id-atg-geographic-scope="geographicScopeId">
          <span
            class="MyMapIndicateur__titreText"
            :class="{ 'MyMapIndicateur__titreText--minddevel': shouldHighlightMinddevelIndicator }"
          >
            {{ libelleDynamique(indicateur.atgTitre, indicateur.atgTitreEn) }}
          </span>
          <span
            v-if="indicateur.atgDescription"
            ref="infoIconRef"
            class="MyMapIndicateur__infoIcon"
            :data-tooltip="indicateur.atgDescription"
            @mouseenter="updateTooltipPosition"
            @mouseleave="hideTooltip"
          >
            <Icon icon="mdi:information" size="14" />
            <span v-if="showTooltip" class="MyMapIndicateur__tooltip" :style="tooltipStyle">
              {{ indicateur.atgDescription }}
            </span>
            <span
              v-if="showTooltip"
              class="MyMapIndicateur__tooltipArrow"
              :style="arrowStyle"
            ></span>
          </span>
        </span>
      </div>
      <button
        v-if="isClickable"
        class="MyMapIndicateur__toggleButton"
        @click.stop="toggleVisibility"
        :aria-label="
          isVisible ? $t('myMap.rightSidebar.hideLayer') : $t('myMap.rightSidebar.showLayer')
        "
        type="button"
      >
        <Icon
          :icon="isVisible ? 'mdi:eye' : 'mdi:eye-off'"
          :color="isVisible ? 'main-blue' : 'grey'"
          :size="20"
        />
      </button>
    </div>
    <InventoryCircleAnalysis
      v-if="inventoryAnalysisConfig && isVisible"
      :indicator-key="indicateur.atgId"
      :view-layer-name="inventoryAnalysisConfig.viewLayer"
      :type-property-name="inventoryAnalysisConfig.typeProperty"
      :is-active="isVisible"
      :competence-id="competence?.cptId"
    />
  </div>
</template>

<script setup lang="ts">
import type { Attribut } from '@/models/interfaces/Attribut'
import type { Competence } from '@/models/interfaces/Competence'
import Icon from '@/components/ui/Icon.vue'
import { getIndicateurIcon } from '@/data/domaineCompetenceIcons'
import InventoryCircleAnalysis from '@/components/map/InventoryCircleAnalysis.vue'
import { i18nInstance } from '@/plugins/i18n'
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import { CompetenceTypeOuvrageService } from '@/services/competenceTypeOuvrage/CompetenceTypeOuvrageService'
import { InfrastructureService } from '@/services/infrastructure/InfrastructureService'
import MapService from '@/services/map/MapService'
import { MartinStyleService, getMartinViewLayerFromScope } from '@/services/map/MartinStyleService'
import { martinTilesBaseUrl } from '@/config/env'
import { useMyMapStore } from '@/stores/myMapStore'
import type maplibregl from 'maplibre-gl'
import { libelleDynamique } from '@/utils/libelleDynamique'

const props = defineProps<{
  indicateur: Attribut
  competence?: Competence
  domaineCode: string
  infrastructureLayerName?: string
  competenceAttributs?: Attribut[]
}>()

interface InventoryAnalysisConfig {
  viewLayer: string
  typeProperty: string
}

const INDICATOR_AEP_COMPLETION = 322
const MINDDEVEL_INDICATOR_DESCRIPTION = 'Indicateur du MINDDEVEL'

const mapStore = useMyMapStore()

const isClickable = computed(() => {
  const hasGeographicScope =
    typeof props.indicateur.atgGeographicScope === 'string' &&
    props.indicateur.atgGeographicScope.trim() !== ''
  const hasStyleJson =
    props.indicateur.atgStyleJson != null &&
    typeof props.indicateur.atgStyleJson === 'object' &&
    Object.keys(props.indicateur.atgStyleJson).length > 0
  const hasPreset = (props.indicateur.fkIndicateurPresetId ?? 0) > 0
  return hasGeographicScope || hasStyleJson || hasPreset
})

const indicateurIcon = computed(
  () => props.indicateur.atgIcon ?? getIndicateurIcon(props.indicateur.atgTitre),
)

const shouldHighlightMinddevelIndicator = computed(() => {
  const description = props.indicateur.atgDescription?.trim()
  const isCentralGeoportal = mapStore.lockedCommuneName == null
  return isCentralGeoportal && description === MINDDEVEL_INDICATOR_DESCRIPTION
})

const geographicScopeId = computed(() => {
  const scope = props.indicateur.atgGeographicScope ?? ''
  const domaineCode = props.domaineCode ?? 'ENV_RN'
  const geomTarget = props.indicateur.atgGeomTarget ?? null
  return getMartinViewLayerFromScope(scope, domaineCode, geomTarget)
})

const geographicScope = computed(() => {
  const scope = props.indicateur.atgGeographicScope
  if (scope != null && scope !== '') {
    return scope
  }
  return 'GENERAL'
})

const inventoryAnalysisConfig = ref<InventoryAnalysisConfig | null>(null)

/** Layer ids from the applied style (backend or atg_style_json). Use these for visibility toggle; do not rewrite ids. */
const appliedCommuneLayerIds = ref<{ fillId: string; lineId: string } | null>(null)

const infoIconRef = ref<HTMLElement | null>(null)
const showTooltip = ref(false)
const tooltipStyle = ref<Record<string, string>>({})
const arrowStyle = ref<Record<string, string>>({})
const isVisible = ref(false)
let checkInterval: ReturnType<typeof setInterval> | null = null

const updateTooltipPosition = () => {
  if (!infoIconRef.value) return

  const rect = infoIconRef.value.getBoundingClientRect()
  const spacing = 8
  const minMargin = 10
  const tooltipMaxWidth = 400
  const iconCenterX = rect.left + rect.width / 2

  let tooltipLeft = iconCenterX
  let transformX = '-50%'

  if (iconCenterX - tooltipMaxWidth / 2 < minMargin) {
    tooltipLeft = minMargin + tooltipMaxWidth / 2
    transformX = '0'
  } else if (iconCenterX + tooltipMaxWidth / 2 > window.innerWidth - minMargin) {
    tooltipLeft = window.innerWidth - minMargin - tooltipMaxWidth / 2
    transformX = '0'
  }

  tooltipStyle.value = {
    left: `${tooltipLeft}px`,
    top: `${rect.top - spacing}px`,
    transform: `translateX(${transformX}) translateY(-100%)`,
  }

  arrowStyle.value = {
    left: `${iconCenterX}px`,
    top: `${rect.top}px`,
    transform: 'translateX(-50%)',
    borderTopColor: 'rgba(0, 0, 0, 0.9)',
    borderBottomColor: 'transparent',
  }

  showTooltip.value = true
}

const hideTooltip = () => {
  showTooltip.value = false
}

function hasStoreOnlyVisibleEntry(): boolean {
  const entry = mapStore.visibleIndicatorEntries.find((e) => e.id === props.indicateur.atgId)
  if (!entry) return false
  return !entry.layerIds || entry.layerIds.length === 0
}

async function restoreInventoryConfigForActiveEntry(): Promise<void> {
  if (inventoryAnalysisConfig.value || !hasStoreOnlyVisibleEntry()) return

  const style = await MartinStyleService.loadStyleFromBackend(props.indicateur.atgId)
  if (!style) return

  inventoryAnalysisConfig.value = resolveInventoryAnalysisConfig(
    style as {
      metadata?: unknown
      sources?: Record<string, unknown>
    },
    props.indicateur.atgId,
  )
}

const checkVisibility = () => {
  const map = mapStore.map
  if (!map) {
    return
  }

  const styleId = props.indicateur.atgId.toString()

  // Resolve commune fill layer id: use applied style ids first (from GET /api/attributs/{id}/style), else convention
  const fillLayerId = appliedCommuneLayerIds.value?.fillId ?? `indicator-${styleId}-fill`
  const infrastructureLayerId = `infrastructure-indicator-${styleId}`

  // Check commune layers first
  const fillLayer = map.getLayer(fillLayerId)
  if (fillLayer) {
    const visibility = map.getLayoutProperty(fillLayerId, 'visibility')
    isVisible.value = visibility === 'visible' || visibility === undefined
    if (isVisible.value) {
      const lineId = appliedCommuneLayerIds.value?.lineId
      mapStore.addVisibleIndicator({
        id: props.indicateur.atgId,
        name: libelleDynamique(props.indicateur.atgTitre, props.indicateur.atgTitreEn),
        layerIds: [fillLayerId, lineId].filter((x): x is string => Boolean(x)),
      })
    } else {
      mapStore.removeVisibleIndicator(props.indicateur.atgId)
    }
    return
  }

  // Check infrastructure layer (unique per indicator)
  const infrastructureLayer = map.getLayer(infrastructureLayerId)
  if (infrastructureLayer) {
    const visibility = map.getLayoutProperty(infrastructureLayerId, 'visibility')
    // Also check badge layer visibility if it exists
    const badgeLayerId = `indicator-${styleId}-badge`
    const badgeLayer = map.getLayer(badgeLayerId)
    if (badgeLayer) {
      const badgeVisibility = map.getLayoutProperty(badgeLayerId, 'visibility')
      isVisible.value =
        (visibility === 'visible' || visibility === undefined) &&
        (badgeVisibility === 'visible' || badgeVisibility === undefined)
    } else {
      isVisible.value = visibility === 'visible' || visibility === undefined
    }
    if (isVisible.value) {
      const badgeLayerId = `indicator-${styleId}-badge`
      mapStore.addVisibleIndicator({
        id: props.indicateur.atgId,
        name: libelleDynamique(props.indicateur.atgTitre, props.indicateur.atgTitreEn),
        layerIds: [infrastructureLayerId, badgeLayerId],
      })
    } else {
      mapStore.removeVisibleIndicator(props.indicateur.atgId)
    }
    return
  }

  // Some indicators (e.g. inventory analysis) are tracked in legend without persistent layerIds.
  if (hasStoreOnlyVisibleEntry()) {
    isVisible.value = true
    return
  }

  // If layer doesn't exist yet, assume hidden by default
  isVisible.value = false
  mapStore.removeVisibleIndicator(props.indicateur.atgId)
}

const toggleVisibility = async () => {
  const myMap = mapStore.myMap
  if (!myMap?.map) {
    return
  }

  const map = myMap.map
  const styleId = props.indicateur.atgId.toString()
  const newVisibility = !isVisible.value
  const visibility = newVisibility ? 'visible' : 'none'

  // Commune indicator: use applied style layer ids (do not rewrite)
  const fillLayerId = appliedCommuneLayerIds.value?.fillId ?? `indicator-${styleId}-fill`
  const outlineLayerId = appliedCommuneLayerIds.value?.lineId ?? `indicator-${styleId}-outline`
  const fillLayer = map.getLayer(fillLayerId)

  if (fillLayer) {
    map.setLayoutProperty(fillLayerId, 'visibility', visibility)
    if (outlineLayerId && map.getLayer(outlineLayerId)) {
      map.setLayoutProperty(outlineLayerId, 'visibility', visibility)
    }
    isVisible.value = newVisibility
    if (newVisibility) {
      const lineId = appliedCommuneLayerIds.value?.lineId
      mapStore.addVisibleIndicator({
        id: props.indicateur.atgId,
        name: libelleDynamique(props.indicateur.atgTitre, props.indicateur.atgTitreEn),
        layerIds: [fillLayerId, lineId].filter((x): x is string => Boolean(x)),
      })
    } else {
      mapStore.removeVisibleIndicator(props.indicateur.atgId)
    }
  } else {
    // Infrastructure indicator - toggle layer for this specific indicator
    const infrastructureLayerId = `infrastructure-indicator-${styleId}`
    const badgeLayerId = `indicator-${styleId}-badge`
    const infrastructureLayer = map.getLayer(infrastructureLayerId)

    if (infrastructureLayer) {
      // Layer exists, just toggle visibility
      map.setLayoutProperty(infrastructureLayerId, 'visibility', visibility)

      // Also toggle badge layer if it exists
      const badgeLayer = map.getLayer(badgeLayerId)
      if (badgeLayer) {
        map.setLayoutProperty(badgeLayerId, 'visibility', visibility)
      }

      isVisible.value = newVisibility
      if (newVisibility) {
        const badgeLayerId = `indicator-${styleId}-badge`
        mapStore.addVisibleIndicator({
          id: props.indicateur.atgId,
          name: libelleDynamique(props.indicateur.atgTitre, props.indicateur.atgTitreEn),
          layerIds: [infrastructureLayerId, badgeLayerId],
        })
      } else {
        mapStore.removeVisibleIndicator(props.indicateur.atgId)
      }
    } else if (inventoryAnalysisConfig.value || hasStoreOnlyVisibleEntry()) {
      // Indicator 324 (and any with inventoryAnalysis): no map layer to toggle, visibility is driven only by isVisible (InventoryCircleAnalysis mounts/unmounts)
      isVisible.value = newVisibility
      if (newVisibility) {
        mapStore.addVisibleIndicator({
          id: props.indicateur.atgId,
          name: libelleDynamique(props.indicateur.atgTitre, props.indicateur.atgTitreEn),
        })
      } else {
        mapStore.removeVisibleIndicator(props.indicateur.atgId)
      }
    } else if (newVisibility) {
      // Layer doesn't exist and we want to show it - create it by calling handleClick
      await handleClick()
    }
  }
}

onMounted(() => {
  window.addEventListener('scroll', hideTooltip, true)
  window.addEventListener('resize', hideTooltip)

  // Initialize visibility check - default to hidden
  isVisible.value = false
  if (hasStoreOnlyVisibleEntry()) {
    isVisible.value = true
    void restoreInventoryConfigForActiveEntry()
  }

  // Wait a bit for map and layers to be ready
  setTimeout(() => {
    checkVisibility()

    // Monitor visibility changes from the map
    const map = mapStore.map
    if (map) {
      const styleId = props.indicateur.atgId.toString()
      const fillLayerId = appliedCommuneLayerIds.value?.fillId ?? `indicator-${styleId}-fill`
      const infrastructureLayerId = `infrastructure-indicator-${styleId}`

      // Check periodically for visibility (MapLibre doesn't provide direct event)
      checkInterval = setInterval(() => {
        if (map.getLayer(fillLayerId) || map.getLayer(infrastructureLayerId)) {
          checkVisibility()
        }
      }, 500)
    }
  }, 100)
})

watch(
  () => mapStore.visibleIndicatorEntries,
  (entries) => {
    const stillVisible = entries.some((e) => e.id === props.indicateur.atgId)
    if (stillVisible && !isVisible.value) {
      isVisible.value = true
      void restoreInventoryConfigForActiveEntry()
      return
    }
    if (!stillVisible && isVisible.value) {
      isVisible.value = false
    }
  },
  { deep: true },
)

onUnmounted(() => {
  window.removeEventListener('scroll', hideTooltip, true)
  window.removeEventListener('resize', hideTooltip)

  if (checkInterval) {
    clearInterval(checkInterval)
  }
})

function buildMinimalStyleFromDto(
  dto: Record<string, unknown>,
  styleId: string,
  viewName: string,
): { version: number; sources: Record<string, unknown>; layers: maplibregl.LayerSpecification[] } {
  const fillColor = (dto.fillColor as string) || '#22c55e'
  const outlineColor = (dto.outlineColor as string) || '#808080'
  const sourceId = `indicator-${styleId}`
  return {
    version: 8,
    sources: {
      [sourceId]: {
        type: 'vector',
        tiles: [`__MARTIN_BASE_URL__/${viewName}/{z}/{x}/{y}`],
        minzoom: 0,
        maxzoom: 22,
      },
    },
    layers: [
      {
        id: `indicator-${styleId}-fill`,
        type: 'fill',
        source: sourceId,
        'source-layer': viewName,
        paint: {
          'fill-color': fillColor,
          'fill-opacity': 0.8,
        },
        layout: { visibility: 'visible' },
      },
      {
        id: `indicator-${styleId}-outline`,
        type: 'line',
        source: sourceId,
        'source-layer': viewName,
        paint: {
          'line-color': outlineColor,
          'line-width': 0.5,
          'line-opacity': 0.8,
        },
        layout: { visibility: 'visible' },
      },
    ],
  }
}

const handleClick = async () => {
  if (!isClickable.value || !mapStore.myMap) {
    return
  }

  if (isVisible.value) {
    await toggleVisibility()
    return
  }

  try {
    const styleId = props.indicateur.atgId.toString()
    let style: {
      layers?: maplibregl.LayerSpecification[]
      sources?: Record<string, unknown>
      metadata?: unknown
    } | null = null

    // Prefer GET /api/attributs/{id}/style and use response as-is (unique ids, correct tile URL).
    style = await MartinStyleService.loadStyleFromBackend(props.indicateur.atgId)
    if (
      !style &&
      props.indicateur.atgStyleJson &&
      typeof props.indicateur.atgStyleJson === 'object' &&
      Object.keys(props.indicateur.atgStyleJson).length > 0
    ) {
      const raw = props.indicateur.atgStyleJson as Record<string, unknown>
      if (raw !== null && ('fillColor' in raw || 'outlineColor' in raw)) {
        style = buildMinimalStyleFromDto(raw, styleId, geographicScopeId.value)
      } else {
        style = raw as {
          layers?: maplibregl.LayerSpecification[]
          sources?: Record<string, unknown>
          metadata?: unknown
        }
      }
    }

    if (!style) {
      console.error(`[MyMapIndicateur] No style for indicator ${styleId}`)
      return
    }

    // Log indicator filter from API response (verify backend sends filter so only features with this indicator are shown)
    if (style.layers?.length) {
      const filterInfo = style.layers.map((l) => ({
        id: l.id,
        type: l.type,
        filter: (l as maplibregl.LayerSpecification & { filter?: unknown }).filter ?? null,
        hasIndicatorFilter:
          Array.isArray((l as maplibregl.LayerSpecification & { filter?: unknown }).filter) &&
          JSON.stringify(
            (l as maplibregl.LayerSpecification & { filter?: unknown }).filter,
          ).includes('"has"'),
      }))
      console.info('[MyMapIndicateur] indicator filter check (from API style)', {
        atgId: styleId,
        atgRefCode: props.indicateur.atgRefCode,
        layers: filterInfo,
      })
    }

    // inventoryAnalysis metadata enables circle analysis without indicator-specific logic.
    // For indicator 322, fallback to inferred viewLayer + type_ouvrage if metadata is missing.
    inventoryAnalysisConfig.value = resolveInventoryAnalysisConfig(style, props.indicateur.atgId)

    // Determine type based on JSON layers
    // Commune indicators (polygons) have fill/line layers
    // Infrastructure indicators (points) have empty layers array
    // Complétude: always display as markers (points) per infrastructure, not polygons
    const metadata = (style.metadata ?? {}) as Record<string, unknown>
    const isCompletudeIndicator =
      metadata.indicator_type === 'completude' ||
      (typeof metadata.preset_code === 'string' && metadata.preset_code.startsWith('completude')) ||
      (typeof props.indicateur.atgRefCode === 'string' &&
        props.indicateur.atgRefCode.toLowerCase().includes('completude'))
    let isCommuneIndicator =
      style.layers?.some((layer) => layer.type === 'fill' || layer.type === 'line') || false
    if (isCompletudeIndicator) {
      isCommuneIndicator = false
    }

    if (isCommuneIndicator) {
      await MartinStyleService.applyStyleObjectToMap(
        mapStore.myMap,
        style as maplibregl.StyleSpecification,
        { useStyleAsIs: true, layerGroup: MapService.LAYER_GROUP.INDICATOR },
      )

      // Show layers when clicking on the card: use layer ids from the applied style (do not rewrite ids).
      if (mapStore.myMap?.map && style.layers?.length) {
        const map = mapStore.myMap.map
        const fillLayer = style.layers.find((l) => l.type === 'fill')
        const lineLayer = style.layers.find((l) => l.type === 'line')
        if (fillLayer?.id) {
          appliedCommuneLayerIds.value = {
            fillId: fillLayer.id,
            lineId: lineLayer?.id ?? '',
          }
          if (map.getLayer(fillLayer.id)) {
            map.setLayoutProperty(fillLayer.id, 'visibility', 'visible')
            // Verify filter is active on the map (same as API style)
            try {
              const fillFilter = map.getFilter(fillLayer.id)
              const outlineFilter = lineLayer?.id ? map.getFilter(lineLayer.id) : null
              console.info('[MyMapIndicateur] indicator filter on map (getFilter)', {
                fillId: fillLayer.id,
                fillFilter,
                outlineId: lineLayer?.id ?? null,
                outlineFilter,
              })
            } catch {
              // getFilter can throw if layer has no filter
            }
          }
        }
        if (lineLayer?.id && map.getLayer(lineLayer.id)) {
          map.setLayoutProperty(lineLayer.id, 'visibility', 'visible')
        }

        // Update visibility state
        isVisible.value = true
        const layerIds = [fillLayer!.id, lineLayer?.id].filter((x): x is string => Boolean(x))
        mapStore.addVisibleIndicator({
          id: props.indicateur.atgId,
          name: libelleDynamique(props.indicateur.atgTitre, props.indicateur.atgTitreEn),
          layerIds,
        })
      }

      // Add popup handler if configured in metadata
      if (style.metadata && typeof style.metadata === 'object') {
        const metadata = style.metadata as Record<string, unknown>
        if (metadata.popup && typeof metadata.popup === 'object') {
          const popupConfig = metadata.popup as Record<string, unknown>
          const attribute = popupConfig.attribute as string | undefined
          const title = (popupConfig.title as string) || undefined
          const unit = (popupConfig.unit as string) || undefined
          const format =
            (popupConfig.format as 'number' | 'percentage' | 'text' | 'table') || 'number'
          const decimals = (popupConfig.decimals as number) || 2

          if (attribute && mapStore.myMap?.map) {
            const fillLayer = style.layers?.find((l) => l.type === 'fill')
            if (fillLayer) {
              const map = mapStore.myMap.map
              const sourceId = fillLayer.source as string
              const sourceLayer = (
                fillLayer as maplibregl.LayerSpecification & { 'source-layer'?: string }
              )['source-layer']

              let hoveredFeatureId: string | number | undefined = undefined

              // Add hover handlers for highlight
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const mousemoveHandler = (e: any) => {
                if (e.features && e.features.length > 0) {
                  const feature = e.features[0]
                  const featureId = feature.id || feature.properties?.id

                  if (featureId !== undefined && featureId !== null) {
                    if (hoveredFeatureId !== featureId) {
                      // Remove highlight from previous feature
                      if (hoveredFeatureId !== undefined && hoveredFeatureId !== null) {
                        try {
                          map.setFeatureState(
                            {
                              source: sourceId,
                              sourceLayer: sourceLayer,
                              id: hoveredFeatureId,
                            },
                            { hover: false },
                          )
                        } catch (error) {
                          console.error('[MyMapIndicateur] Error setting hover false:', error)
                        }
                      }

                      // Highlight current feature
                      hoveredFeatureId = featureId
                      try {
                        map.setFeatureState(
                          {
                            source: sourceId,
                            sourceLayer: sourceLayer,
                            id: hoveredFeatureId,
                          },
                          { hover: true },
                        )
                      } catch (error) {
                        console.error('[MyMapIndicateur] Error setting hover true:', error)
                      }
                    }

                    map.getCanvas().style.cursor = 'pointer'
                  }
                }
              }

              const mouseleaveHandler = () => {
                if (hoveredFeatureId !== undefined && hoveredFeatureId !== null) {
                  try {
                    map.setFeatureState(
                      {
                        source: sourceId,
                        sourceLayer: sourceLayer,
                        id: hoveredFeatureId,
                      },
                      { hover: false },
                    )
                  } catch (error) {
                    console.error('[MyMapIndicateur] Error setting hover false on leave:', error)
                  }
                  hoveredFeatureId = undefined
                }
                map.getCanvas().style.cursor = ''
              }

              map.on('mousemove', fillLayer.id, mousemoveHandler)
              map.on('mouseleave', fillLayer.id, mouseleaveHandler)

              // Import and create popup component
              const IndicatorPopup = (await import('@/views/map/components/IndicatorPopup.vue'))
                .default
              const { createApp, h } = await import('vue')

              // Use only the key from the API style (metadata.popup.attribute); do not derive from atgRefCode.
              const resolveValue = (propsMap: Record<string, unknown>) => {
                if (
                  attribute != null &&
                  propsMap[attribute] !== undefined &&
                  propsMap[attribute] !== null
                )
                  return propsMap[attribute]
                return propsMap['valeur_calculee']
              }

              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              map.on('click', fillLayer.id, (e: any) => {
                if (e.features && e.features.length > 0) {
                  const feature = e.features[0]
                  const properties = feature.properties || {}
                  const value = resolveValue(properties as Record<string, unknown>)
                  console.log('[MyMapIndicateur] click fill', {
                    atgId: props.indicateur.atgId,
                    popupAttribute: attribute,
                    featureProperties: properties,
                    resolvedValue: value,
                  })

                  // Get coordinates from feature geometry
                  const coordinates = e.lngLat

                  // Create popup component instance
                  const popupComponent = h(IndicatorPopup, {
                    title: title ?? '',
                    value: value as number | string | boolean | null | undefined,
                    unit: unit ?? '',
                    format,
                    decimals,
                  })

                  const app = createApp({
                    render: () => popupComponent,
                  })
                  app.use(i18nInstance)
                  const container = document.createElement('div')
                  app.mount(container)

                  // Add popup to map
                  if (mapStore.myMap?.addPopup) {
                    mapStore.myMap.addPopup(coordinates, container, false)
                  }
                }
              })
            }
          }
        }
      }
    } else {
      // Indicators with inventoryAnalysis (e.g. 324) are drawn only by InventoryCircleAnalysis (clusters + pictograms). Do not add the infrastructure points layer to avoid duplicate white points at all zoom levels.
      if (inventoryAnalysisConfig.value) {
        isVisible.value = true
        mapStore.addVisibleIndicator({
          id: props.indicateur.atgId,
          name: libelleDynamique(props.indicateur.atgTitre, props.indicateur.atgTitreEn),
        })
        return
      }
      // Use infrastructure points layer - extract layer name from source tiles URL
      const sourceKey = Object.keys(style.sources || {})[0]
      const source = sourceKey ? style.sources?.[sourceKey] : null
      const tilesUrl =
        source && typeof source === 'object' && 'tiles' in source && Array.isArray(source.tiles)
          ? source.tiles[0]
          : ''

      // Prefer layer name from API style URL (works with __MARTIN_BASE_URL__ or resolved https:// URL); fallback to geographicScopeId, then prop, then domain-derived view.
      let layerName = ''
      if (tilesUrl) {
        const urlMatch = tilesUrl.match(/\/([^\/]+)\/\{z\}/)
        if (urlMatch && urlMatch[1]) {
          layerName = urlMatch[1]
        }
      }
      if (!layerName) {
        layerName =
          geographicScopeId.value ||
          props.infrastructureLayerName ||
          `view_infrastructures_${props.domaineCode.toLowerCase()}`
      }

      // Create unique layer for this indicator
      if (!mapStore.myMap?.map) {
        return
      }

      const map = mapStore.myMap.map
      const infrastructureLayerId = `infrastructure-indicator-${styleId}`
      const sourceId = `infrastructure-indicator-${styleId}-source`

      // Per-indicator source so the tiles URL always matches the API style. Prefer API tiles URL; only build from layerName when no tiles URL.
      if (props.infrastructureLayerName && map.getSource(sourceId)) {
        if (map.getLayer(infrastructureLayerId)) map.removeLayer(infrastructureLayerId)
        const badgeLayerIdToRemove = `indicator-${styleId}-badge`
        if (map.getLayer(badgeLayerIdToRemove)) map.removeLayer(badgeLayerIdToRemove)
        map.removeSource(sourceId)
      }
      if (!map.getSource(sourceId)) {
        const baseUrl = (martinTilesBaseUrl ?? '')
          .replace(/(https?:\/)\/{2,}/g, '$1/')
          .replace(/\/+$/, '')
        const hasApiTilesUrl =
          tilesUrl && (tilesUrl.startsWith('http') || tilesUrl.includes('__MARTIN_BASE_URL__'))
        const urlToUse = hasApiTilesUrl
          ? tilesUrl.replace('__MARTIN_BASE_URL__', martinTilesBaseUrl ?? '')
          : props.infrastructureLayerName || layerName
            ? `${baseUrl}/${props.infrastructureLayerName || layerName}/{z}/{x}/{y}`
            : ''
        if (urlToUse) {
          const cleanUrl = urlToUse.replace(/(https?:\/)\/{2,}/g, '$1/').replace(/\/+$/, '')
          mapStore.myMap.addVectorTileSource(sourceId, cleanUrl, 0, 22)
        }
      }

      // Remove existing layers if they exist
      if (map.getLayer(infrastructureLayerId)) {
        map.removeLayer(infrastructureLayerId)
      }

      // Also remove badge layer if it exists
      const badgeLayerId = `indicator-${styleId}-badge`
      if (map.getLayer(badgeLayerId)) {
        map.removeLayer(badgeLayerId)
      }

      // Load marker configuration (default for fallback and for type_ouvrage default image)
      const { loadMarkerConfig: loadMarkerConfigFn } = await import(
        '@/services/map/InfrastructureLayerService'
      )
      const markerConfig = await loadMarkerConfigFn(props.indicateur.atgId, layerName)

      const iconImageName = `infrastructure-marker-${props.indicateur.atgId}`
      const markerColor = markerConfig.markerColor || '#3aa2bc'
      const lucideIcon = markerConfig.lucideIcon
      const iconColor = markerConfig.iconColor || '#3aa2bc'
      const iconSize = markerConfig.iconSize || 0.6
      const showBadge = markerConfig.showBadge || false
      const badgeColor = markerConfig.badgeColor || '#a7b6c2'

      let iconImageLayoutValue: string | maplibregl.Expression = iconImageName

      try {
        const { getTypeToMarkerConfigMap, ensureTypeOuvrageImagesOnMap } = await import(
          '@/services/map/TypeOuvrageMarkerService'
        )
        const typeToConfig = await getTypeToMarkerConfigMap()
        const typeOuvrageResult = await ensureTypeOuvrageImagesOnMap(map, styleId, typeToConfig, {
          markerColor,
          lucideIcon,
          iconColor,
          iconSize,
          showBadge,
          badgeColor,
        })
        const typeToImageId = typeOuvrageResult.typeToImageId
        const defaultImageId = typeOuvrageResult.defaultImageId
        if (Object.keys(typeToImageId).length > 0) {
          const matchArgs: unknown[] = ['match', ['get', 'type_ouvrage']]
          for (const [typeCode, imageId] of Object.entries(typeToImageId)) {
            matchArgs.push(typeCode, imageId)
          }
          matchArgs.push(defaultImageId)
          iconImageLayoutValue = matchArgs as unknown as maplibregl.Expression
        }
      } catch (err) {
        console.warn(
          '[MyMapIndicateur] type_ouvrage markers unavailable, using single marker:',
          err,
        )
      }

      // Ensure single marker image exists when not using type_ouvrage (fallback)
      if (iconImageLayoutValue === iconImageName && !map.hasImage(iconImageName)) {
        const { MarkerGeneratorService } = await import('@/services/map/MarkerGeneratorService')
        const markerSvg = await MarkerGeneratorService.generateMarker(
          markerColor,
          lucideIcon,
          iconColor,
          showBadge,
          badgeColor,
        )
        const img = await MarkerGeneratorService.createImageFromSvg(markerSvg)
        if (img) {
          map.addImage(iconImageName, img)
        }
      }

      // Determine source-layer name
      const sourceLayerName = layerName.includes('.')
        ? layerName.split('.').pop() || layerName
        : layerName

      // Use the same filter as in the API style so only features with this indicator key are shown (e.g. only châteaux d'eau for indicator 407).
      const firstLayerWithFilter = style.layers?.find(
        (l) => (l as maplibregl.LayerSpecification & { filter?: unknown }).filter != null,
      )
      const layerFilter =
        firstLayerWithFilter != null
          ? (firstLayerWithFilter as maplibregl.LayerSpecification & { filter?: unknown }).filter
          : undefined

      // Create unique layer for this indicator
      const pointLayer = {
        id: infrastructureLayerId,
        type: 'symbol' as const,
        source: sourceId,
        'source-layer': sourceLayerName,
        ...(layerFilter != null && { filter: layerFilter as maplibregl.FilterSpecification }),
        layout: {
          'icon-image':
            iconImageLayoutValue as maplibregl.DataDrivenPropertyValueSpecification<string>,
          'icon-size': iconSize,
          'icon-allow-overlap': true,
          'icon-ignore-placement': false,
          'icon-anchor': 'bottom' as const,
          visibility: 'visible' as const,
        },
        metadata: { isPersistent: true, layerGroup: 'indicator' },
      }

      map.addLayer(pointLayer as maplibregl.LayerSpecification)

      // Add badge layer if configured in style metadata, or when marker has showBadge but style has no badge config (frontend fallback)
      const { BadgeLayerService } = await import('@/services/map/BadgeLayerService')
      let badgeConfig = null
      if (style.metadata && typeof style.metadata === 'object') {
        badgeConfig = BadgeLayerService.loadBadgeConfigFromStyle(style)
      }
      const metadata = (style.metadata as Record<string, unknown>) || {}
      const popup = metadata.popup as Record<string, unknown> | undefined
      const hasPopupAttribute = Boolean(popup?.attribute)
      if (!badgeConfig && (showBadge || hasPopupAttribute)) {
        const colorAttribute = (popup?.attribute as string) || 'pourc_completude_fiche'
        badgeConfig = {
          enabled: true,
          colorAttribute,
          minzoom: 8,
        }
      }
      if (badgeConfig) {
        await BadgeLayerService.addBadgeLayer(
          map,
          sourceId,
          sourceLayerName,
          props.indicateur.atgId,
          badgeConfig,
          layerFilter as maplibregl.FilterSpecification | undefined,
        )
      }

      // Add popup handler if configured in metadata
      if (style.metadata && typeof style.metadata === 'object') {
        const metadata = style.metadata as Record<string, unknown>
        console.log('[MyMapIndicateur] Metadata found:', metadata)

        if (metadata.popup && typeof metadata.popup === 'object') {
          const popupConfig = metadata.popup as Record<string, unknown>
          const attribute = popupConfig.attribute as string | undefined
          const title = (popupConfig.title as string) || undefined
          const unit = (popupConfig.unit as string) || undefined
          const format =
            (popupConfig.format as 'number' | 'percentage' | 'text' | 'table') || 'number'
          const decimals = (popupConfig.decimals as number) || 2

          console.log('[MyMapIndicateur] Popup config:', {
            attribute,
            title,
            unit,
            format,
            decimals,
            infrastructureLayerId,
          })

          if (attribute) {
            // Import and create popup component
            const IndicatorPopup = (await import('@/views/map/components/IndicatorPopup.vue'))
              .default
            const { createApp, h } = await import('vue')

            // Use only the key from the API style (metadata.popup.attribute); do not derive from atgRefCode.
            const resolveValueInfra = (propsMap: Record<string, unknown>) => {
              if (
                attribute != null &&
                propsMap[attribute] !== undefined &&
                propsMap[attribute] !== null
              )
                return propsMap[attribute]
              return propsMap['valeur_calculee']
            }

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map.on('click', infrastructureLayerId, async (e: any) => {
              if (!e.features || e.features.length === 0) {
                return
              }
              const feature = e.features[0]
              const properties = feature.properties || {}

              const popupTitle = properties.ift_intitule || title

              let popupValue: unknown = resolveValueInfra(properties as Record<string, unknown>)
              console.log('[MyMapIndicateur] click infra', {
                atgId: props.indicateur.atgId,
                popupAttribute: attribute,
                featureProperties: properties,
                resolvedValue: popupValue,
              })

              if (format === 'table') {
                const tableData: Record<string, unknown> = {}
                Object.keys(properties).forEach((key) => {
                  if (key === key.toUpperCase() && key !== key.toLowerCase()) {
                    const value = properties[key]
                    if (value !== undefined && value !== null) {
                      tableData[key] = value
                    }
                  }
                })
                if (Object.keys(tableData).length === 0 && typeof popupValue === 'string') {
                  try {
                    const parsed = JSON.parse(popupValue)
                    if (typeof parsed === 'object' && parsed !== null) {
                      popupValue = parsed
                    }
                  } catch {
                    // keep popupValue as-is
                  }
                } else if (Object.keys(tableData).length > 0) {
                  popupValue = tableData
                }
              }

              const EMPTY_ATTRIBUTE_PLACEHOLDER = '—'
              function isAttributValueFilled(val: unknown): boolean {
                if (val === undefined || val === null) return false
                const s = String(val).trim()
                return s !== '' && s !== EMPTY_ATTRIBUTE_PLACEHOLDER
              }

              const idInfra =
                properties.ift_id ?? properties.id ?? properties.gid ?? properties.ogc_fid
              const idInfrastructure =
                idInfra !== undefined && idInfra !== null
                  ? typeof idInfra === 'number'
                    ? idInfra
                    : String(idInfra)
                  : undefined

              const typeOuvrageCodeRaw = properties.type_ouvrage ?? properties.TYPE_OUVRAGE
              const typeOuvrageCode =
                typeOuvrageCodeRaw != null ? String(typeOuvrageCodeRaw).trim() : ''
              const competenceId = props.competence?.cptId

              // Resolve attributs from feature: tile keys = atg_ref_code (same as atgRefCode). Prefer properties.attributs; else build from root using expected attributs' atgRefCode only.
              function getAttributsFromFeature(
                propsMap: Record<string, unknown>,
                expectedAttributs: { atgRefCode: string }[],
              ): Record<string, unknown> {
                const nested = propsMap.attributs ?? propsMap.ATTRIBUTS
                if (nested != null && typeof nested === 'object' && !Array.isArray(nested)) {
                  return nested as Record<string, unknown>
                }
                const out: Record<string, unknown> = {}
                const refCodes = new Set(expectedAttributs.map((a) => a.atgRefCode))
                refCodes.forEach((refCode) => {
                  if (propsMap[refCode] !== undefined) {
                    out[refCode] = propsMap[refCode]
                  }
                })
                return out
              }

              let expectedAttributs: Attribut[] = []
              if (competenceId != null && typeOuvrageCode !== '') {
                try {
                  expectedAttributs =
                    await CompetenceTypeOuvrageService.getAttributsForCompetenceAndType(
                      competenceId,
                      typeOuvrageCode,
                    )
                } catch {
                  // keep empty, fallback below
                }
              }

              const attributsFromFeature =
                expectedAttributs.length > 0
                  ? getAttributsFromFeature(
                      properties as Record<string, unknown>,
                      expectedAttributs,
                    )
                  : ((): Record<string, unknown> => {
                      const raw = properties.attributs ?? properties.ATTRIBUTS
                      if (raw != null && typeof raw === 'object' && !Array.isArray(raw)) {
                        return raw as Record<string, unknown>
                      }
                      const fallback: Record<string, unknown> = {}
                      Object.keys(properties).forEach((key) => {
                        if (key === key.toUpperCase() && key !== key.toLowerCase()) {
                          const v = properties[key]
                          if (v !== undefined && v !== null) fallback[key] = v
                        }
                      })
                      return fallback
                    })()

              if (format === 'table' && Object.keys(attributsFromFeature).length > 0) {
                popupValue = attributsFromFeature
              }

              let tableBasedVisibleCount: number | undefined
              let tableBasedTotalCount: number | undefined
              if (expectedAttributs.length > 0) {
                tableBasedVisibleCount = expectedAttributs.filter((a) =>
                  isAttributValueFilled(attributsFromFeature[a.atgRefCode]),
                ).length
                tableBasedTotalCount = expectedAttributs.length
              } else if (
                format === 'table' &&
                popupValue != null &&
                typeof popupValue === 'object' &&
                !Array.isArray(popupValue)
              ) {
                const tableEntries = Object.entries(popupValue)
                tableBasedTotalCount = tableEntries.length
                tableBasedVisibleCount = tableEntries.filter(([, v]) =>
                  isAttributValueFilled(v),
                ).length
              }

              let attributsRows: Array<{ label: string; value: string }> = []
              if (expectedAttributs.length > 0) {
                attributsRows = expectedAttributs
                  .filter((a) => isAttributValueFilled(attributsFromFeature[a.atgRefCode]))
                  .map((a) => ({
                    label: libelleDynamique(a.atgTitre, a.atgTitreEn) || a.atgRefCode,
                    value: String(attributsFromFeature[a.atgRefCode] ?? ''),
                  }))
              } else {
                attributsRows = Object.entries(attributsFromFeature)
                  .filter(([, v]) => v !== undefined && v !== null && String(v).trim() !== '')
                  .map(([label, v]) => ({
                    label,
                    value: typeof v === 'number' ? String(v) : String(v),
                  }))
              }

              const visibleAttributesCount = tableBasedVisibleCount ?? attributsRows.length

              let totalAttributesCount = tableBasedTotalCount ?? visibleAttributesCount
              if (tableBasedTotalCount == null) {
                if (expectedAttributs.length > 0) {
                  totalAttributesCount = expectedAttributs.length
                } else if (competenceId != null && typeOuvrageCode !== '') {
                  try {
                    const attributs =
                      await CompetenceTypeOuvrageService.getAttributsForCompetenceAndType(
                        competenceId,
                        typeOuvrageCode,
                      )
                    totalAttributesCount = attributs.length
                  } catch {
                    if (idInfrastructure != null) {
                      try {
                        const attributeValues =
                          await InfrastructureService.getAttributeValues(idInfrastructure)
                        if (Array.isArray(attributeValues)) {
                          totalAttributesCount = attributeValues.length
                        } else if (
                          attributeValues != null &&
                          typeof attributeValues === 'object' &&
                          !Array.isArray(attributeValues)
                        ) {
                          const hydraMember = (attributeValues as Record<string, unknown>)[
                            'hydra:member'
                          ]
                          totalAttributesCount = Array.isArray(hydraMember)
                            ? hydraMember.length
                            : Object.keys(attributeValues).length
                        }
                      } catch {
                        // keep totalAttributesCount = visibleAttributesCount
                      }
                    }
                  }
                } else if (idInfrastructure != null) {
                  try {
                    const attributeValues =
                      await InfrastructureService.getAttributeValues(idInfrastructure)
                    if (Array.isArray(attributeValues)) {
                      totalAttributesCount = attributeValues.length
                    } else if (
                      attributeValues != null &&
                      typeof attributeValues === 'object' &&
                      !Array.isArray(attributeValues)
                    ) {
                      const hydraMember = (attributeValues as Record<string, unknown>)[
                        'hydra:member'
                      ]
                      totalAttributesCount = Array.isArray(hydraMember)
                        ? hydraMember.length
                        : Object.keys(attributeValues).length
                    }
                  } catch {
                    // keep totalAttributesCount = visibleAttributesCount
                  }
                }
              }

              const coordinates = e.lngLat

              const originalResolvedNum =
                typeof resolveValueInfra(properties as Record<string, unknown>) === 'number'
                  ? (resolveValueInfra(properties as Record<string, unknown>) as number)
                  : NaN
              const popupValueNum =
                typeof popupValue === 'number'
                  ? popupValue
                  : typeof popupValue === 'string'
                    ? Number(popupValue)
                    : NaN
              const gaugeCandidate = !Number.isNaN(popupValueNum)
                ? popupValueNum
                : originalResolvedNum
              const completudeAttr = (attribute ?? '').toLowerCase()
              const isCompletionPercentage =
                !Number.isNaN(gaugeCandidate) &&
                gaugeCandidate >= 0 &&
                gaugeCandidate <= 100 &&
                (completudeAttr === 'pourc_completude_fiche' ||
                  completudeAttr.includes('completude'))

              const shouldShowGauge =
                format === 'percentage' ||
                (unit === '%' &&
                  (typeof popupValue === 'number' ||
                    (typeof popupValue === 'string' && !Number.isNaN(Number(popupValue))))) ||
                isCompletionPercentage
              const gaugeValue =
                shouldShowGauge && isCompletionPercentage && typeof popupValue !== 'number'
                  ? gaugeCandidate
                  : popupValue

              const popupComponent = h(IndicatorPopup, {
                title: popupTitle || undefined,
                value: gaugeValue as string | number | null | undefined,
                unit: shouldShowGauge && isCompletionPercentage ? '%' : unit,
                format: shouldShowGauge && isCompletionPercentage ? 'percentage' : format,
                decimals,
                idInfrastructure,
                showFicheAttributsButton: idInfrastructure != null,
                visibleAttributesCount,
                totalAttributesCount,
                attributsRows: attributsRows.length > 0 ? attributsRows : undefined,
                showGauge: shouldShowGauge,
                onOpenFiche:
                  idInfrastructure != null
                    ? (id: string | number) =>
                        mapStore.openInfrastructureFicheModal(
                          id,
                          popupTitle ?? undefined,
                          props.competence?.cptId,
                          properties as Record<string, unknown>,
                        )
                    : undefined,
              })

              const app = createApp({ render: () => popupComponent })
              app.use(i18nInstance)
              const container = document.createElement('div')
              app.mount(container)

              if (mapStore.myMap?.addPopup) {
                mapStore.myMap.addPopup(coordinates, container, false)
              }
            })

            console.log(
              '[MyMapIndicateur] Click handler registered for layer:',
              infrastructureLayerId,
            )
          } else {
            console.warn('[MyMapIndicateur] No attribute specified in popup config')
          }
        } else {
          console.log('[MyMapIndicateur] No popup config found in metadata')
        }
      } else {
        console.log('[MyMapIndicateur] No metadata found in style')
      }

      // Show layer when clicking on the card
      isVisible.value = true
      mapStore.addVisibleIndicator({
        id: props.indicateur.atgId,
        name: libelleDynamique(props.indicateur.atgTitre, props.indicateur.atgTitreEn),
        layerIds: [infrastructureLayerId, badgeLayerId],
      })
    }
  } catch (error) {
    console.error('Error loading indicator layer:', error)
  }
}

const extractInventoryAnalysisConfig = (style: {
  metadata?: unknown
}): InventoryAnalysisConfig | null => {
  const metadata = style.metadata
  if (!metadata || typeof metadata !== 'object') {
    return null
  }

  const inventoryAnalysis = (metadata as Record<string, unknown>).inventoryAnalysis
  if (!inventoryAnalysis || typeof inventoryAnalysis !== 'object') {
    return null
  }

  const viewLayer = (inventoryAnalysis as Record<string, unknown>).viewLayer
  const typeProperty = (inventoryAnalysis as Record<string, unknown>).typeProperty

  if (typeof viewLayer !== 'string' || typeof typeProperty !== 'string') {
    return null
  }

  return { viewLayer, typeProperty }
}

const resolveInventoryAnalysisConfig = (
  style: {
    metadata?: unknown
    sources?: Record<string, unknown>
  },
  atgId: number,
): InventoryAnalysisConfig | null => {
  const extracted = extractInventoryAnalysisConfig(style)
  if (extracted) {
    return extracted
  }

  // Indicator 322: fallback config for clustering-by-color when metadata.inventoryAnalysis is missing.
  if (Number(atgId) === INDICATOR_AEP_COMPLETION) {
    const firstSource = Object.values(style.sources ?? {})[0]
    let inferredViewLayer = ''
    if (
      firstSource &&
      typeof firstSource === 'object' &&
      'tiles' in firstSource &&
      Array.isArray((firstSource as { tiles?: unknown }).tiles)
    ) {
      const firstTile = (firstSource as { tiles?: unknown[] }).tiles?.[0]
      if (typeof firstTile === 'string') {
        const urlMatch = firstTile.match(/\/([^\/]+)\/\{z\}/)
        if (urlMatch?.[1]) {
          inferredViewLayer = urlMatch[1]
        }
      }
    }
    if (inferredViewLayer) {
      return {
        viewLayer: inferredViewLayer,
        typeProperty: 'pourc_completude_fiche',
      }
    }
  }

  return null
}
</script>

<style lang="scss">
.MyMapIndicateur {
  display: flex;
  flex-flow: column nowrap;
  gap: 0.25rem;
  padding: 0.625rem 0.75rem;
  min-height: 2.5rem;
  border-bottom: 1px solid var(--neutral-border);
  background: rgb(var(--v-theme-surface));
  transition: border-left-color 0.2s;

  &:last-child {
    border-bottom: none;
  }

  &--clickable {
    cursor: pointer;
  }

  &--active {
    border-left: 3px solid rgb(var(--v-theme-main-blue));
    background: var(--panel-row-active-bg);
  }

  &__content {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 0.5rem;
    min-height: var(--panel-row-min-height);
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__text {
    display: flex;
    flex-flow: column nowrap;
    gap: 0.125rem;
    flex: 1;
    min-width: 0;
  }

  &__toggleButton {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 2rem;
    min-height: 2rem;
    padding: 0.375rem;
    border: none;
    background: transparent;
    cursor: pointer;
    border-radius: var(--dim-radius);
    transition: background-color 0.2s;
    flex-shrink: 0;

    &:hover {
      background-color: var(--neutral-hover);
    }

    &:focus,
    &:focus-visible {
      outline: 2px solid rgb(var(--v-theme-main-blue));
      outline-offset: 2px;
    }
  }

  &__titre {
    min-width: 0;
    font-size: var(--panel-label-size);
    font-weight: 500;
    color: rgb(var(--v-theme-main-blue));
    display: flex;
    align-items: flex-start;
    gap: 0.25rem;
  }

  &__titreText {
    min-width: 0;
    word-break: break-word;

    &--minddevel {
      color: #c62828;
      font-weight: 700;
    }
  }

  &__infoIcon {
    position: relative;
    cursor: help;
    color: rgb(var(--v-theme-main-blue));
    display: inline-flex;
    align-items: center;
    vertical-align: super;
    font-size: 0.75rem;
    line-height: 1;
    flex-shrink: 0;
    margin-top: -0.125rem;
  }

  &__tooltip {
    position: fixed;
    padding: 0.75rem 1rem;
    background: rgba(0, 0, 0, 0.95);
    color: white;
    font-size: 0.8125rem;
    white-space: pre-line;
    line-height: 1.6;
    border-radius: 6px;
    pointer-events: none;
    z-index: 10000;
    min-width: 250px;
    max-width: min(400px, calc(100vw - 2rem));
    width: max-content;
    word-break: break-word;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
    text-align: left;
  }

  &__tooltipArrow {
    position: fixed;
    border: 5px solid transparent;
    pointer-events: none;
    z-index: 10001;
  }
}
</style>
